import { composeAnesthesiaWindow, type AnesthesiaWindowFields } from './anesthesiaWindow';

/**
 * Translates the form's per-claim coding object into the wire/storage one (feature 044).
 * Only the anesthesia window differs: the form holds four fields
 * (`anesthesiaStartDate`/`anesthesiaStartTime`/`anesthesiaEndDate`/`anesthesiaEndTime`),
 * the wire holds two instants (`anesthesiaStart`/`anesthesiaEnd`). Every other member —
 * ICD-10, CPT, ASA classification, ASA codes — passes through untouched.
 *
 * Shared by the portal's submit and edit services and the back-office save path so one
 * mapping is written and tested once (Principle 11).
 *
 * The reverse direction is `splitAnesthesiaWindow`, called where an encounter is loaded —
 * it needs only the window, since the rest of the object is already form-shaped.
 */
export function codingToWire(
  coding: (Partial<AnesthesiaWindowFields> & object) | null | undefined,
): Record<string, unknown> {
  if (!coding) return {};

  const rest: Record<string, unknown> = { ...coding };
  delete rest.anesthesiaStartDate;
  delete rest.anesthesiaStartTime;
  delete rest.anesthesiaEndDate;
  delete rest.anesthesiaEndTime;

  // An incomplete window contributes nothing, so a half-entered window is never persisted
  // (FR-018). The form schema reports it as a validation error before this point.
  const window = composeAnesthesiaWindow(coding);
  return window ? { ...rest, ...window } : rest;
}
